import type { VisualSignalEvent, VisualSignalName } from '../signals/VisualSignal.js';
import type { VisualLayerConfig } from '../layers/VisualLayer.js';
import type { VisualRecipe } from '../recipe/VisualRecipe.js';

/**
 * Changes the stack while the song plays.
 *
 * `SWITCH` is the mode where a piece has sections rather than a single setting.
 * The director watches for a trigger and rotates which layers are enabled, so
 * the same recipe reads as several different treatments across one song.
 *
 * It deliberately does *not* rebuild renderers. Enabling and disabling existing
 * layers is instant and keeps every seed and every accumulated bit of state;
 * tearing down and reconstructing on a bar line would drop the artwork's history
 * four times a phrase, which is the opposite of what §11 asks for.
 *
 * The three triggers implemented are the three the brief requires. Adding
 * `SCORE_THRESHOLD` or `TIME` later means one more case in `matches`, because
 * the director only ever asks "did this event fire".
 */

export const SWITCH_TRIGGERS = [
  'BAR',
  'SECTION',
  'COMBO_BREAK',
  'BEAT',
  'NOTE_HIT',
  'NOTE_MISS',
] as const;

export type SwitchTrigger = (typeof SWITCH_TRIGGERS)[number];

export function isSwitchTrigger(value: string): value is SwitchTrigger {
  return (SWITCH_TRIGGERS as readonly string[]).includes(value);
}

export interface SwitchDirectorOptions {
  readonly trigger?: SwitchTrigger;
  /** Trigger occurrences to let pass between switches. */
  readonly every?: number;
  /**
   * Layers that never switch out.
   *
   * The world underneath, normally. Without an anchor a switch can leave the
   * frame empty, which reads as a failure rather than as a change.
   */
  readonly anchorRenderers?: readonly string[];
}

export interface SwitchDecision {
  /** Layer ids that should now be enabled. */
  readonly enabled: readonly string[];
  readonly group: number;
  readonly trigger: SwitchTrigger;
}

export class SwitchDirector {
  private readonly trigger: SwitchTrigger;
  private readonly every: number;
  private readonly anchors: readonly string[];

  private groups: string[][] = [];
  private anchorIds: string[] = [];
  private occurrences = 0;
  private group = 0;
  private switches = 0;

  constructor(options: SwitchDirectorOptions = {}) {
    this.trigger = options.trigger ?? 'BAR';
    this.every = Math.max(1, options.every ?? 4);
    this.anchors = options.anchorRenderers ?? ['MANDALA'];
  }

  /**
   * Divides a recipe's layers into the groups it will rotate between.
   *
   * One group per non-anchor layer is the simplest division that produces a
   * visible change on every switch; a recipe with a single decorative layer
   * therefore alternates between "with it" and "without it".
   */
  prepare(recipe: VisualRecipe): void {
    this.anchorIds = recipe.layers
      .filter((entry) => this.anchors.includes(entry.renderer))
      .map((entry) => entry.id);

    const rotating = recipe.layers
      .filter((entry) => !this.anchors.includes(entry.renderer))
      .map((entry) => entry.id);

    this.groups = rotating.length === 0 ? [[]] : rotating.map((id) => [id]);
    this.occurrences = 0;
    this.group = 0;
    this.switches = 0;
  }

  /** The layers that should be on right now. */
  currentSelection(): readonly string[] {
    const group = this.groups[this.group] ?? [];
    return [...this.anchorIds, ...group];
  }

  /**
   * Feeds one event in.
   *
   * Returns a decision when the stack should change, and null the rest of the
   * time — which is almost always, since a bar line arrives once every few
   * seconds and beats arrive constantly.
   */
  handle(event: VisualSignalEvent): SwitchDecision | null {
    if (!this.fires(event.name)) return null;
    this.occurrences += 1;
    if (this.occurrences % this.every !== 0) return null;
    if (this.groups.length <= 1 && this.anchorIds.length === 0) return null;

    this.group = (this.group + 1) % this.groups.length;
    this.switches += 1;
    return {
      enabled: this.currentSelection(),
      group: this.group,
      trigger: this.trigger,
    };
  }

  /**
   * A combo break, which is not a signal on the bus.
   *
   * It is a fact about the run that only the runtime can notice — the bus
   * carries `NOTE_MISS`, but a miss with no combo to break is a different
   * event from one that ends a streak of forty.
   */
  handleComboBreak(): SwitchDecision | null {
    if (this.trigger !== 'COMBO_BREAK') return null;
    this.occurrences += 1;
    if (this.occurrences % this.every !== 0) return null;
    if (this.groups.length === 0) return null;

    this.group = (this.group + 1) % this.groups.length;
    this.switches += 1;
    return { enabled: this.currentSelection(), group: this.group, trigger: 'COMBO_BREAK' };
  }

  private fires(name: VisualSignalName): boolean {
    // COMBO_BREAK never arrives as a bus signal; it comes through the method above.
    return this.trigger !== 'COMBO_BREAK' && name === this.trigger;
  }

  diagnostics(): {
    readonly trigger: SwitchTrigger;
    readonly every: number;
    readonly group: number;
    readonly groups: number;
    readonly switches: number;
    readonly enabled: readonly string[];
  } {
    return {
      trigger: this.trigger,
      every: this.every,
      group: this.group,
      groups: this.groups.length,
      switches: this.switches,
      enabled: this.currentSelection(),
    };
  }
}

/** Applies a decision to a recipe, producing the layer patches to make. */
export function applySelection(
  layers: readonly VisualLayerConfig[],
  enabled: readonly string[],
): { id: string; enabled: boolean }[] {
  return layers.map((entry) => ({ id: entry.id, enabled: enabled.includes(entry.id) }));
}
